import { useEffect, useState } from 'react';
import useApiFetch from '../../hooks/useApiFetch';
import { Modal, Typography, Button, Space, Card, Col, Row, Radio, Checkbox, Tooltip, message, Spin } from 'antd';
import { useAuth } from '../../context/auth.context';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
const { Title, Text } = Typography;

// Utility function to load Razorpay script dynamically
const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        // Check if Razorpay is already loaded
        if (window.Razorpay) {
            resolve(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
            resolve(true);
        };
        script.onerror = () => {
            resolve(false);
        };
        document.body.appendChild(script);
    });
};

export default function TrialExpiredModal() {
    // Main modal state
    const [modalType, setModalType] = useState(null); // 'freeTrial', 'expired', 'pricing', 'expiryWarning'
    const [previousModalType, setPreviousModalType] = useState(null); // Track where we came from

    // Pricing modal state
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedAddon, setSelectedAddon] = useState({});
    const [loading, setLoading] = useState(false);
    const [locationDetected, setLocationDetected] = useState(false);
    const [pollingForSubscription, setPollingForSubscription] = useState(false);

    // Loading modal state
    const [showLoadingModal, setShowLoadingModal] = useState(false);

    // Expiry warning state
    const [daysLeft, setDaysLeft] = useState(0);
    const [nextPlanName, setNextPlanName] = useState('');

    const { currentUser, apiClient, subscriptionPlan } = useAuth();

    const [userCurrency, setUserCurrency] = useState(currentUser?.organization?.preferredCurrency || 'INR');
    const [addons, setAddons] = useState([]);
    const history = useHistory();

    // Fetch plans data
    const {
        data: plans,
        loading: isLoading,
        error,
    } = useApiFetch('api/subscriptions/pricing-plans', (res) => res.data);

    const plansWithPrices =
        plans?.filter((plan) => {
            return plan.prices && plan.prices.length > 0;
        }) || [];

    // Fetch addon data
    useEffect(() => {
        const fetchAddons = async () => {
            if (!selectedPlan) return;

            try {
                const res = await apiClient.get(`api/subscriptions/addons?planId=${selectedPlan}`);
                setAddons(res.data.data || []);
            } catch (err) {
                console.error('Failed to fetch addons:', err);
                setAddons([]);
            }
        };

        fetchAddons();
    }, [selectedPlan, apiClient]);
    // const filteredAddons = addons.filter((addon) => getPriceForUser(addon, true));

    // Currency detection
    // const detectUserCurrency = () => {
    //     try {
    //         fetch('https://ipapi.co/json/')
    //             .then((response) => response.json())
    //             .then((data) => {
    //                 console.log('IP-based location:', data);
    //                 if (data.country_code === 'IN' || data.country === 'India') {
    //                     setUserCurrency('INR');
    //                     setLocationDetected(true);
    //                     console.log('Currency set to INR based on IP location');
    //                     return;
    //                 } else {
    //                     setUserCurrency(data.currency);
    //                     setLocationDetected(true);
    //                 }
    //             })
    //             .catch((ipError) => {
    //                 console.log('IP-based location detection failed:', ipError);
    //             });

    //         console.log('No immediate India indicators found, defaulting to USD');
    //         setUserCurrency('USD');
    //     } catch (error) {
    //         console.error('Error detecting user currency:', error);
    //         setUserCurrency('USD');
    //     }
    // };

    // Helper function to check if expiry warning was shown today
    const wasExpiryWarningShownToday = () => {
        const lastShownDate = localStorage.getItem('expiryWarningLastShown');
        if (!lastShownDate) return false;

        const today = moment().format('YYYY-MM-DD');
        return lastShownDate === today;
    };

    // Helper function to mark expiry warning as shown today
    const markExpiryWarningShownToday = () => {
        const today = moment().format('YYYY-MM-DD');
        localStorage.setItem('expiryWarningLastShown', today);
    };

    // Initialize modal state and currency detection
    useEffect(() => {
        if (!currentUser) return;

        if (subscriptionPlan) {
            return null; // Don't show modal if user has a plan
        }

        // const subscription = currentUser.orgSubscription;
        const orgUsedTrial = !currentUser.organization?.freeTrialUsed;
        const hasSubscription = !!subscriptionPlan;

        if (orgUsedTrial && !hasSubscription) {
            setModalType('freeTrial');
        } else if (subscriptionPlan) {
            const nextPlan = subscriptionPlan?.nextPlan;
            const expiryDate = moment(subscriptionPlan?.expiryDate);
            const today = moment();
            const daysUntilExpiry = expiryDate.diff(today, 'days');

            setNextPlanName(nextPlan?.name || 'your next plan');
            setDaysLeft(daysUntilExpiry);

            if (
                subscriptionPlan.isFreeTrial &&
                subscriptionPlan.status === 'active' &&
                daysUntilExpiry <= 3 &&
                daysUntilExpiry >= 0
            ) {
                if (!wasExpiryWarningShownToday()) {
                    setModalType('expiryWarning');
                }
            } else if (subscriptionPlan.status === 'active') {
                if (expiryDate.isBefore(today)) {
                    setModalType('expired');
                    if (window.location.pathname !== '/admin') {
                        history.push('/admin');
                    }
                }
            } else if (subscriptionPlan?.status !== 'active') {
                setModalType('expired');
                if (window.location.pathname !== '/admin') {
                    history.push('/admin');
                }
            }
        }

        // detectUserCurrency();
    }, [currentUser, plans, subscriptionPlan]);

    // Handle modal close
    const handleModalClose = () => {
        setModalType(null);
        setPreviousModalType(null);
        setSelectedPlan(null);
        setSelectedAddon({});
        setLoading(false);
        setPollingForSubscription(false);
        setShowLoadingModal(false);
    };

    // Handle expiry warning modal close
    const handleExpiryWarningClose = () => {
        markExpiryWarningShownToday();
        setModalType(null);
    };

    // Helper functions
    const getUserCurrency = () => {
        return userCurrency;
    };

    const getPriceForUser = (plan) => {
        const preferredCurrency = getUserCurrency();
        let priceData = plan.prices.find((p) => p.currency === preferredCurrency);
        if (!priceData) {
            priceData = plan.prices.find((p) => p.currency === 'USD');
        }
        if (!priceData && plan.prices.length > 0) {
            priceData = plan.prices[0];
        }
        return priceData;
    };

    const formatPrice = (price, currency) => {
        const symbols = {
            INR: '₹',
            USD: '$',
            EUR: '€',
            SGD: 'S$',
        };
        return `${symbols[currency] || currency}${price}`;
    };

    // Filter plans based on selected currency
    const getFilteredPlans = () => {
        return plansWithPrices.filter((plan) => {
            return plan.prices.some((price) => price.currency === userCurrency);
        });
    };

    // Subscription polling
    const pollSubscriptionStatus = async (maxAttempts = 30, intervalSeconds = 5) => {
        let attempts = 0;
        setPollingForSubscription(true);
        setShowLoadingModal(true);

        const poll = async () => {
            try {
                attempts++;
                console.log(`Polling attempt ${attempts}/${maxAttempts}`);

                const response = await apiClient.get('/api/org-users/me');
                const currentUser = response.data?.data[0];

                if (currentUser?.orgSubscription?.status === 'active') {
                    const expiryDate = moment(currentUser.orgSubscription.expiryDate);
                    if (!expiryDate.isBefore(moment.now())) {
                        setPollingForSubscription(false);
                        setShowLoadingModal(false);
                        message.success('Subscription activated successfully!');
                        window.location.reload();
                        return;
                    }
                }

                if (attempts >= maxAttempts) {
                    setPollingForSubscription(false);
                    setShowLoadingModal(false);
                    message.warning('Subscription activation is taking longer than expected. Please refresh the page.');
                    return;
                }

                setTimeout(poll, intervalSeconds * 1000);
            } catch (error) {
                console.error('Error polling subscription status:', error);
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(poll, intervalSeconds * 1000);
                } else {
                    setPollingForSubscription(false);
                    setShowLoadingModal(false);
                    message.error('Failed to check subscription status. Please refresh the page.');
                }
            }
        };

        setTimeout(poll, 2000);
    };

    // Payment handlers
    const handlePaymentSuccess = async (paymentResponse, subscriptionDraft) => {
        console.log('Payment successful, starting subscription polling...', paymentResponse);
        setLoading(false);

        pollSubscriptionStatus();
    };

    const handleProceedToPayment = async () => {
        if (!selectedPlan) {
            message.error('Please select a plan');
            return;
        }

        try {
            setLoading(true);

            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded) {
                message.error('Failed to load payment system. Please try again.');
                setLoading(false);
                return;
            }

            const selectedPlanData = plans.find((plan) => plan.id === selectedPlan);
            if (!selectedPlanData) {
                message.error('Selected plan not found');
                setLoading(false);
                return;
            }

            const priceData = getPriceForUser(selectedPlanData);
            if (!priceData) {
                message.error('Pricing information not available for selected currency');
                setLoading(false);
                return;
            }

            const selectedAddons = selectedAddon[selectedPlan] || [];

            const response = await apiClient.put('/api/subscriptions/update-plan', {
                planId: selectedPlan,
                currency: userCurrency,
                billingCycle: priceData.billingCycle,
                addons: selectedAddons.map((addon) => ({ addonId: addon.id })),
            });

            console.log('API Response:', response.data);

            const data = response.data?.data?.[0];
            const subscriptionDraft = data?.subscriptionDraft;
            const razorpaySubscription = data?.razorpaySubscription;

            if (!subscriptionDraft || !razorpaySubscription) {
                console.error('Invalid subscription data received:', { data, subscriptionDraft, razorpaySubscription });
                message.error('Failed to create subscription. Please try again.');
                setLoading(false);
                return;
            }

            try {
                const options = {
                    key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                    subscription_id: razorpaySubscription.id,
                    name: 'CommKraft',
                    description: `${subscriptionDraft.plan.name} Plan`,
                    image: '/logo192.png',
                    handler: (response) => {
                        try {
                            console.log('Payment successful:', response);
                            handlePaymentSuccess(response, subscriptionDraft);
                        } catch (successError) {
                            console.error('Error in success handler:', successError);
                        } finally {
                            setLoading(false);
                        }
                    },
                    prefill: {
                        name: (currentUser?.firstName || '') + ' ' + (currentUser?.lastName || ''),
                        email: currentUser?.email || '',
                        contact: currentUser?.phoneNumber || '',
                    },
                    notes: {
                        organizationId: currentUser?.organizationId,
                        orgCodeName: currentUser?.organization?.codeName,
                        subscriptionDraftId: subscriptionDraft.id,
                        type: 'free-trial-subscription',
                        rzSubscriptionId: razorpaySubscription.id,
                    },
                    theme: { color: '#1890ff' },
                    modal: {
                        ondismiss: () => {
                            console.warn('Payment modal dismissed by user');
                            setLoading(false);
                        },
                    },
                    retry: { enabled: true, max_count: 3 },
                };

                const rzp = new window.Razorpay(options);
                console.log('Razorpay initialized with options:', options);

                rzp.on('payment.failed', (response) => {
                    console.error('Payment failed:', response.error);
                    message.error('Payment failed. Please try again.');
                    setLoading(false);
                });

                rzp.open();
                console.log('Razorpay checkout opened');
            } catch (razorpayError) {
                console.error('Razorpay initialization error:', razorpayError);
                message.error('Failed to open payment gateway. Please try again.');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error creating subscription:', error);
            message.error('Failed to process payment. Please try again.');
            setLoading(false);
        }
    };

    const createFreePlan = async () => {
        try {
            setLoading(true);

            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded) {
                message.error('Failed to load payment system. Please try again.');
                setLoading(false);
                return;
            }

            const response = await apiClient.post('/api/subscriptions/free-trial', {
                currency: userCurrency,
            });
            const data = response.data?.data[0];
            const subscriptionDraft = data?.subscriptionDraft;
            const razorpaySubscription = data?.razorpaySubscription;

            if (subscriptionDraft && razorpaySubscription) {
                const options = {
                    key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                    subscription_id: razorpaySubscription.id,
                    name: 'CommKraft',
                    description: `${subscriptionDraft.plan.name} Plan - 7 Day Free Trial`,
                    image: '/logo192.png',
                    handler: function (response) {
                        console.log('Payment successful:', response);
                        handlePaymentSuccess(response, subscriptionDraft);
                    },
                    prefill: {
                        name: currentUser?.firstName + ' ' + currentUser?.lastName || '',
                        email: currentUser?.email || '',
                        contact: currentUser?.phoneNumber || '',
                    },
                    notes: {
                        organizationId: currentUser?.organizationId,
                        orgCodeName: currentUser?.organization?.codeName,
                        subscriptionDraftId: subscriptionDraft.id,
                        rzSubscriptionId: razorpaySubscription.id,
                        type: 'free-trial-subscription',
                    },
                    theme: {
                        color: '#1890ff',
                    },
                    modal: {
                        ondismiss: function () {
                            console.log('Payment modal dismissed');
                            setLoading(false);
                        },
                    },
                    retry: {
                        enabled: true,
                        max_count: 3,
                    },
                };

                const rzp = new window.Razorpay(options);
                rzp.on('payment.failed', function (response) {
                    console.error('Payment failed:', response.error);
                    setLoading(false);
                    message.error('Payment failed. Please try again.');
                });
                rzp.open();
            } else {
                console.error('Invalid subscription data received');
                setLoading(false);
                message.error('Failed to create subscription. Please try again.');
            }
        } catch (error) {
            console.error('Error creating free plan:', error);
            setLoading(false);
            message.error('Failed to process payment. Please try again.');
        }
    };

    const handleAddonChange = (planId, addonList) => {
        setSelectedAddon((prev) => ({
            ...prev,
            [planId]: addonList,
        }));
    };

    // Prepare pricing data with currency filtering
    const filteredPlans = getFilteredPlans();
    const pricingCardData =
        filteredPlans &&
        filteredPlans.map((plan) => {
            const priceData = plan.prices.find((p) => p.currency === userCurrency);
            return {
                id: plan.id,
                name: plan.name,
                description: plan.description,
                price: priceData ? formatPrice(priceData.price, priceData.currency) : 'Free',
                billingCycle: priceData ? priceData.billingCycle.toLowerCase() : '',
                currency: priceData ? priceData.currency : null,
                rawPrice: priceData ? priceData.price : 0,
                features: plan.features.map((f) => {
                    const limitText = f.type === 'limit' && f.limit ? ` - Limit: ${f.limit}` : '';
                    return `${f.feature.name}${limitText}`;
                }),
                addons: plan.features.flatMap(
                    (f) =>
                        f.feature.addons?.map((addon) => ({
                            id: addon.id,
                            name: `${f.feature.name} Addon – ${formatPrice(
                                addon.unitPrice,
                                priceData?.currency || 'USD',
                            )}`,
                            description: f.feature.description || 'No description available',
                            unitPrice: addon.unitPrice,
                            featureName: f.feature.name,
                        })) || [],
                ),
            };
        });
    const filteredAddons = addons.filter((addon) => getPriceForUser(addon, true));

    // Loading Modal Component with Smooth Transitions
    const LoadingModal = () => {
        const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
        const messages = [
            'Please wait while we process your request.',
            'Securing your data and preparing your dashboard...',
            'Almost there! Just a moment more...',
        ];
        useEffect(() => {
            if (!showLoadingModal) return;

            const interval = setInterval(() => {
                setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
            }, 2500); // Change message every 2.5 seconds

            return () => clearInterval(interval);
        }, [showLoadingModal]);

        return (
            <Modal
                open={showLoadingModal}
                centered
                closable={false}
                footer={null}
                zIndex={999999}
                width={400}
                height={200}
            >
                <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }} align="center">
                    <Spin />
                    <Title level={4} style={{ margin: 0 }}>
                        Loading...
                    </Title>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                        {messages[currentMessageIndex]}
                    </Text>
                </Space>
            </Modal>
        );
    };

    // Don't render if no modal type is set
    if (!modalType) return null;

    return (
        <>
            {/* Loading Modal */}
            <LoadingModal />

            {/* Free Trial Expiry Warning Modal */}
            {modalType === 'expiryWarning' && (
                <Modal
                    open={true}
                    centered
                    closable={true}
                    maskClosable={true}
                    onCancel={handleExpiryWarningClose}
                    footer={null}
                    zIndex={999999}
                    width={500}
                >
                    <Space
                        direction="vertical"
                        size="large"
                        style={{ width: '100%', textAlign: 'center' }}
                        align="center"
                    >
                        <Title level={3}>Free Trial Expiring Soon</Title>
                        <Text type="secondary" style={{ fontSize: '16px', lineHeight: '1.6', maxWidth: 400 }}>
                            Your free trial will expire in{' '}
                            <strong>
                                {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                            </strong>
                            . You will be automatically upgraded to <strong>"{nextPlanName}"</strong>.
                        </Text>
                        <Space size="middle" style={{ marginTop: '16px' }}>
                            <Button
                                type="primary"
                                size="large"
                                onClick={() => {
                                    markExpiryWarningShownToday();
                                    setModalType(null);
                                    history.push('/admin/my-subscription');
                                }}
                            >
                                View Plan
                            </Button>
                            <Button
                                type="default"
                                size="large"
                                // style={{ minWidth: 150 }}
                                onClick={handleExpiryWarningClose}
                            >
                                Remind Me Tomorrow
                            </Button>
                        </Space>
                    </Space>
                </Modal>
            )}

            {/* Free Trial Modal */}
            {modalType === 'freeTrial' && (
                <Modal
                    open={true}
                    centered
                    closable={true}
                    maskClosable={true}
                    onCancel={handleModalClose}
                    footer={null}
                    zIndex={999999}
                >
                    <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: '100%', textAlign: 'center' }}
                        align="center"
                    >
                        <Title level={3}>7 Day Free Trial</Title>
                        <Text type="secondary" style={{ maxWidth: 400 }}>
                            Please provide your credit card details to start your 7-day free trial. You can cancel
                            anytime before the trial ends.
                        </Text>
                        <Space size="middle">
                            <Button
                                loading={loading}
                                type="primary"
                                size="large"
                                onClick={createFreePlan}
                                disabled={pollingForSubscription}
                            >
                                {pollingForSubscription ? 'Activating Trial...' : 'Start Free Trial'}
                            </Button>
                            <Button
                                type="default"
                                size="large"
                                onClick={() => {
                                    setPreviousModalType('freeTrial');
                                    setModalType('pricing');
                                }}
                                disabled={loading || pollingForSubscription}
                            >
                                Select Plan
                            </Button>
                        </Space>
                    </Space>
                </Modal>
            )}

            {/* Expired Modal (simple view) */}
            {modalType === 'expired' && (
                <Modal
                    open={true}
                    centered
                    closable={true}
                    maskClosable={true}
                    onCancel={handleModalClose}
                    footer={null}
                    zIndex={999999}
                >
                    <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: '100%', textAlign: 'center' }}
                        align="center"
                    >
                        <Title level={3}>Trial Expired</Title>

                        <Text type="secondary" style={{ maxWidth: 400 }}>
                            {currentUser.role === 'admin'
                                ? 'Your 7-day free trial has ended. Please choose a plan to continue using the platform.'
                                : "Your organization's trial has ended. Please contact your administrator for access."}
                        </Text>

                        {currentUser.role === 'admin' && (
                            <Button
                                type="primary"
                                size="large"
                                onClick={() => {
                                    setPreviousModalType('expired');
                                    setModalType('pricing');
                                }}
                            >
                                View Pricing Plans
                            </Button>
                        )}
                    </Space>
                </Modal>
            )}

            {/* Pricing Modal */}
            {modalType === 'pricing' && (
                <Modal
                    open={true}
                    centered
                    closable={true}
                    maskClosable={true}
                    onCancel={handleModalClose}
                    footer={null}
                    zIndex={9999}
                    width={950}
                >
                    <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
                        <Title type="primary" level={3}>
                            Choose Your Plan
                        </Title>
                        <Text type="secondary">
                            Select a plan that best fits your needs
                            {locationDetected && (
                                <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.7 }}>
                                    (Showing prices in {userCurrency})
                                </span>
                            )}
                        </Text>

                        <Space size="small" align="center">
                            <Radio.Group
                                value={userCurrency}
                                onChange={(e) => {
                                    setUserCurrency(e.target.value);
                                    setSelectedPlan(null); // Reset selected plan when currency changes
                                    setSelectedAddon({}); // Reset selected addons
                                }}
                                size="small"
                            >
                                <Radio.Button value="INR">₹ INR</Radio.Button>
                                <Radio.Button value="USD">$ USD</Radio.Button>
                                <Radio.Button value="USD">$ USD</Radio.Button>
                                <Radio.Button value="SGD">S$ SGD</Radio.Button>
                            </Radio.Group>
                        </Space>

                        {/* Show message if no plans available for selected currency */}
                        {pricingCardData && pricingCardData.length === 0 ? (
                            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                                <Title level={4} type="warning">
                                    No plans available in {userCurrency}
                                </Title>
                                <Text type="secondary">
                                    Please select a different currency to view available plans.
                                </Text>
                            </div>
                        ) : (
                            <>
                                <Row
                                    gutter={[16, 16]}
                                    style={{ width: '98%', margin: '0px auto', paddingTop: 10 }}
                                    align="stretch"
                                    justify="center"
                                >
                                    {pricingCardData &&
                                        pricingCardData.map((plan) => {
                                            const totalPlans = pricingCardData.length;
                                            let colSpan = {
                                                xs: 24,
                                                sm: 24,
                                                md: 24,
                                            };

                                            if (totalPlans === 2) {
                                                colSpan = { xs: 24, sm: 12, md: 12 };
                                            } else if (totalPlans === 3) {
                                                colSpan = { xs: 24, sm: 12, md: 8 };
                                            } else if (totalPlans >= 4) {
                                                colSpan = { xs: 24, sm: 12, md: 6 };
                                            }

                                            return (
                                                <Col {...colSpan} key={plan.id}>
                                                    <Card
                                                        hoverable
                                                        onClick={() => setSelectedPlan(plan.id)}
                                                        className={selectedPlan === plan.id ? 'selected-plan' : ''}
                                                        style={{
                                                            border:
                                                                selectedPlan === plan.id
                                                                    ? '1px solid #1890ff'
                                                                    : '1px solid #000',
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'space-between',
                                                            transition: 'all 0.3s ease',
                                                            paddingTop: 12,
                                                            backgroundColor:
                                                                selectedPlan === plan.id ? '#fafafaff' : '#ffffffff',
                                                            boxShadow:
                                                                selectedPlan === plan.id ? '0 2px 3px #1890ff' : 'none',
                                                        }}
                                                    >
                                                        <Space
                                                            direction="vertical"
                                                            size="middle"
                                                            align="center"
                                                            style={{ width: '100%' }}
                                                        >
                                                            <Title
                                                                level={4}
                                                                style={{
                                                                    margin: 0,
                                                                    fontWeight: 'bold',
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.5px',
                                                                    borderBottom: '2px solid #000',
                                                                    paddingBottom: 8,
                                                                }}
                                                            >
                                                                {plan.name}
                                                            </Title>

                                                            <Space direction="vertical" size={0} align="center">
                                                                <Title
                                                                    level={2}
                                                                    style={{
                                                                        margin: 0,
                                                                        fontWeight: 'bold',
                                                                        fontSize: '2.5em',
                                                                    }}
                                                                >
                                                                    {plan.price}
                                                                </Title>
                                                                {plan.billingCycle && (
                                                                    <Text
                                                                        style={{
                                                                            fontStyle: 'italic',
                                                                            fontSize: '14px',
                                                                            textTransform: 'uppercase',
                                                                            letterSpacing: '0.3px',
                                                                        }}
                                                                    >
                                                                        Per {plan.billingCycle}
                                                                    </Text>
                                                                )}
                                                            </Space>

                                                            <Space
                                                                direction="vertical"
                                                                size="small"
                                                                style={{ width: '100%' }}
                                                                align="start"
                                                            >
                                                                {plan.features.map((feature, index) => (
                                                                    <Text
                                                                        key={index}
                                                                        style={{
                                                                            fontSize: 14,
                                                                            fontWeight: '500',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '8px',
                                                                        }}
                                                                    >
                                                                        <span
                                                                            style={{
                                                                                fontWeight: 'bold',
                                                                                fontSize: '16px',
                                                                            }}
                                                                        >
                                                                            ✓
                                                                        </span>
                                                                        {feature}
                                                                    </Text>
                                                                ))}
                                                            </Space>
                                                        </Space>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                </Row>

                                {(() => {
                                    const selected =
                                        pricingCardData && pricingCardData.find((plan) => plan.id === selectedPlan);
                                    if (!selected || !selected.addons || selected.addons.length === 0) return null;
                                    return (
                                        <Space
                                            direction="vertical"
                                            size="large"
                                            style={{
                                                width: '100%',
                                                marginTop: 32,
                                                padding: 16,
                                                borderTop: '1px solid #eee',
                                            }}
                                        >
                                            <Title
                                                level={4}
                                                style={{
                                                    textAlign: 'center',
                                                    textTransform: 'uppercase',
                                                    width: '100%',
                                                }}
                                            >
                                                Add-ons for {selected.name}
                                            </Title>
                                            <Space
                                                direction="vertical"
                                                size="small"
                                                style={{ width: '100%', maxWidth: 600, margin: '0 auto' }}
                                                align="start"
                                            >
                                                {filteredAddons.map((addon) => {
                                                    const { id: addonId, feature } = addon;
                                                    const featureName = feature?.name || 'Unknown Feature';
                                                    const featureDescription = feature?.description || '';
                                                    const priceData = getPriceForUser(addon, true); // Strict pricing

                                                    return (
                                                        <Tooltip
                                                            key={`addon-${addonId}`}
                                                            title={featureDescription}
                                                            placement="topRight"
                                                            zIndex={99999}
                                                        >
                                                            <Checkbox
                                                                value={addonId}
                                                                checked={(selectedAddon[selectedPlan] || []).some(
                                                                    (item) => item.id === addonId,
                                                                )}
                                                                onChange={(e) => {
                                                                    const current = selectedAddon[selectedPlan] || [];
                                                                    const next = e.target.checked
                                                                        ? [...current, addon]
                                                                        : current.filter((item) => item.id !== addonId);
                                                                    handleAddonChange(selectedPlan, next);
                                                                }}
                                                                style={{
                                                                    width: '100%',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                <span style={{ fontSize: 14 }}>{featureName}</span>
                                                            </Checkbox>

                                                            <div style={{ paddingLeft: 24, marginTop: 4 }}>
                                                                <span
                                                                    style={{
                                                                        display: 'inline-block',
                                                                        background: '#f0f0f0',
                                                                        padding: '2px 6px',
                                                                        borderRadius: 4,
                                                                        fontSize: 12,
                                                                        color: '#333',
                                                                    }}
                                                                >
                                                                    {formatPrice(
                                                                        priceData.unitPrice,
                                                                        priceData.currency,
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Space>
                                        </Space>
                                    );
                                })()}
                            </>
                        )}

                        <Space size="middle">
                            <Button
                                size="large"
                                onClick={() => {
                                    // Go back to the previous modal type we came from
                                    if (previousModalType) {
                                        setModalType(previousModalType);
                                        setPreviousModalType(null);
                                    } else {
                                        // Fallback logic if previousModalType is not set
                                        if (!currentUser.orgSubscription) {
                                            setModalType('freeTrial');
                                        } else {
                                            setModalType('expired');
                                        }
                                    }
                                }}
                                disabled={loading || pollingForSubscription}
                            >
                                Back
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleProceedToPayment}
                                loading={loading}
                                disabled={
                                    !selectedPlan ||
                                    pollingForSubscription ||
                                    (pricingCardData && pricingCardData.length === 0)
                                }
                            >
                                {pollingForSubscription ? 'Activating Subscription...' : 'Proceed to Payment'}
                            </Button>
                        </Space>
                    </Space>
                </Modal>
            )}
        </>
    );
}
